// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payables LIST page (Stage 6P.6).
//
// Read-only roll-up of posted Purchase Bill accounting postings.
// One row per POSTED purchase_accounting_postings entry — the backend
// (`purchaseAccountingReports.listSupplierPayables`) is the sole
// authority for which postings qualify, the column shape, and the
// pagination envelope. This page never computes a balance, never
// invents a status, never alters a posting.
//
// Columns (brief):
//   Supplier · Bill No · Invoice No · Posting No · Grand Total ·
//   Paid Amount (0 placeholder) · Balance (= Grand Total) ·
//   Due Date (— placeholder) · Status ("UNPAID").
//
// Toolbar:
//   Search (q over supplier / bill / invoice / posting no) ·
//   Posting date range · Reset / Refresh.
//
// Pagination: 25 / 50 / 100 (offset + limit translated from
// page + pageSize because the backend exposes offset/limit, not
// page/pageSize, on purchaseAccountingReports.listSupplierPayables).
//
// IMPORTANT — payment columns are placeholders. The backend currently
// reports every posted bill as fully UNPAID with balance == grand
// total because supplier payments / paid-amount / balance recalc /
// aging buckets land in a later sub-stage. The UI surfaces that
// reality explicitly via the banner so operators don't read the page
// as a live AR/AP register.
//
// NO payment posting actions on this page (deferred). The row-level
// "..." menu is intentionally absent — there is no destination yet.
//
// Mirrors the compact enterprise AntD list pattern from the inventory
// CurrentStockPage:
//   • slate-50 header strip + uppercase column titles (via .grn-* CSS)
//   • 22 px row density
//   • sticky toolbar
//   • offset/limit pagination
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Input, Space, Spin, Table,
  Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, BankOutlined
} from '@ant-design/icons';

import { purchaseAccountingApi } from './purchaseAccountingApi';
import { normalizeSupplierPayableRow } from './purchaseAccountingHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

// Always render the literal zero — the placeholder paid_amount is a
// real "0", not a missing value, so we want "0.00" rather than "—".
function fmtMoneyZero(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function SupplierPayablesPage() {
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit perm strings cover
  // admin/manager/staff/viewer per userPermissions.js. This page is
  // gated by the dedicated SUPPLIER_PAY_VIEW slot so finance roles
  // can be given payables visibility without full posting access.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView = hasPerm('erp.purchase.supplier_payables.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the bills list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      // Backend exposes offset/limit, not page/pageSize. Translate.
      const limit  = pageSize;
      const offset = (page - 1) * pageSize;
      const params = { limit, offset };

      const needle = (search || '').trim();
      if (needle) params.search = needle;

      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.posting_date_from = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.posting_date_to   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out  = await purchaseAccountingApi.listSupplierPayables(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      const normalized = list
        .map((r) => normalizeSupplierPayableRow(r))
        .filter(Boolean);

      setRows(normalized);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier payables.'),
        key: 'pa-supplier-payables'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onDates = (v) => { setDateRange(v || null); setPage(1); };
  const onReset = () => {
    setSearchInput('');
    setSearch('');
    setDateRange(null);
    setPage(1);
  };

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name = pickField(row, 'supplier_name', 'supplierName');
        const code = pickField(row, 'supplier_code', 'supplierCode');
        if (!name && !code) return <span className="grn-muted">—</span>;
        return (
          <div className="grn-supplier">
            <Tooltip title={name || code}>
              <span className="grn-supplier__name">{name || '—'}</span>
            </Tooltip>
            {code
              ? <span className="grn-muted" style={{ fontSize: 10.5 }}>{code}</span>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Bill No',
      dataIndex: 'purchaseBillNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'purchase_bill_no', 'purchaseBillNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Posting No',
      dataIndex: 'postingNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'posting_no', 'postingNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'grand_total', 'grandTotal'))}
        </span>
      )
    },
    {
      title:     'Paid Amount',
      dataIndex: 'paidAmount',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        // Always render the literal placeholder zero. Real paid-amount
        // bookkeeping arrives in a later sub-stage.
        <span className="grn-mono">
          {fmtMoneyZero(pickField(row, 'paid_amount', 'paidAmount'))}
        </span>
      )
    },
    {
      title:     'Balance',
      dataIndex: 'balance',
      width:     130,
      align:     'right',
      render: (_v, row) => {
        // Backend stubs balance := grand_total. We mirror that and
        // never recompute on the client.
        const balance = pickField(row, 'balance', 'balance');
        const fallback = pickField(row, 'grand_total', 'grandTotal');
        const v = (balance === undefined || balance === null)
          ? fallback
          : balance;
        return (
          <span className="grn-mono grn-line-count">
            {fmtMoney(v)}
          </span>
        );
      }
    },
    {
      title:     'Due Date',
      dataIndex: 'dueDate',
      width:     130,
      render: (_v, row) => {
        // Placeholder — backend currently returns null for due_date.
        const v = pickField(row, 'due_date', 'dueDate');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{fmtDate(v)}</span>;
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     110,
      render: (_v, row) => {
        // Backend stubs status := "UNPAID" for every posted bill.
        const raw = pickField(row, 'status', 'status');
        const key = raw ? String(raw).trim().toUpperCase() : 'UNPAID';
        return <Tag color="warning" className="grn-status-tag">{key}</Tag>;
      }
    }
  ]), []);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view supplier payables."
          description="Ask your master account to grant erp.purchase.supplier_payables.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><BankOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Supplier Payables</span>
            <span className="grn-headstrip__subtitle">
              Posted Purchase Bill accounting postings, one row per bill.
              Read-only. Paid Amount, Balance, Due Date and Status are
              backend placeholders — supplier payment / paid-amount /
              balance recalc / aging buckets land in a later sub-stage.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Banner — make the placeholder shape explicit so operators
          don't read this as a live AR/AP register. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
        message="Payment columns are placeholders"
        description="Every posted bill is reported as fully UNPAID with balance equal to grand total. Supplier payments, paid-amount tracking, due dates and aging are scheduled for a later sub-stage."
      />

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search supplier / bill / invoice / posting no…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['Posting From', 'Posting To']}
          style={{ width: 260 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'posting_id', 'postingId')
              || pickField(r, 'posting_no', 'postingNo')
              || `${pickField(r, 'purchase_bill_no', 'purchaseBillNo') || ''}-${pickField(r, 'supplier_id', 'supplierId') || ''}`
            }
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No posted Purchase Bills yet — post a bill from the Purchase Accounting inbox to populate this register.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
